import { useEffect } from 'react'
import { fetchCustomerMe, refreshCustomerSession } from '../customerAuthApi'
import { hasPersistedIdentity } from '../authPersistence'
import type { CustomerAccount, CustomerProfile } from '../types'

type ApplyAuthenticatedStateFn = (
  token: string,
  account: CustomerAccount,
  profile: CustomerProfile,
) => void

type ClearAuthStateFn = (input?: { clearStorage?: boolean; broadcast?: boolean }) => void

export const useCustomerAuthBootstrap = ({
  applyAuthenticatedState,
  clearAuthState,
  setInitializing,
}: {
  applyAuthenticatedState: ApplyAuthenticatedStateFn
  clearAuthState: ClearAuthStateFn
  setInitializing: (value: boolean) => void
}) => {
  useEffect(() => {
    let active = true

    const bootstrap = async () => {
      try {
        if (!hasPersistedIdentity()) {
          clearAuthState({ clearStorage: false, broadcast: false })
          return
        }

        const session = await refreshCustomerSession()
        if (!active) return
        try {
          const me = await fetchCustomerMe()
          if (!active) return
          applyAuthenticatedState(session.token, me.account, me.profile)
        } catch {
          if (!active) return
          applyAuthenticatedState(session.token, session.account, session.profile)
        }
      } catch {
        if (!active) return
        clearAuthState({ clearStorage: true, broadcast: false })
      } finally {
        if (active) {
          setInitializing(false)
        }
      }
    }

    void bootstrap()
    return () => {
      active = false
    }
  }, [applyAuthenticatedState, clearAuthState, setInitializing])
}
